import React, { useCallback, useState } from "react";
import ReactDOMServer from "react-dom/server";
import { useSelector } from "react-redux";

// region Imports - Libraries
import classNames from "classnames";
import * as dateFns from "date-fns";
import _ from "lodash";
import Loading from "react-fullscreen-loading";
// endregion Imports - Libraries
// region Imports - Utils
import utils from "@utils/useful-functions";
// endregion Imports - Utils
// region Imports - Material-UI
import { Grid } from "@material-ui/core";
import DoneAllTwoToneIcon from "@material-ui/icons/DoneAllTwoTone";
import SearchIcon from "@material-ui/icons/Search";
// endregion Imports - Material-UI
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import {
  AlertMessages,
  DataTableMessages,
  GlobalMessages,
  VehiclesModuleMessages
} from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Shared
import { PaginationProperties } from "@shared/utils/schemas/standard-return-object";
import { ProcessedAlertHistory } from "@shared/interfaces/processed-alert-history.interface";
import { Alert } from "@shared/interfaces/alert.interface";
// endregion Imports - Shared
// region Imports - Components
import NewDataTable, { DataTableActions, DataTableButtons, DataTableColumns } from "@molecules/NewDataTable";
import AlertDialogJustify from "@components/Alert/AlertDialogJustify";
import ButtonTable from "@components/Button/ButtonTable";
import Header from "@components/Header";
import QueryFilterAlertHistory from "@organisms/QueryFilterAlertHistory";
import DialogConfirmAction from "@components/Dialog/ConfirmAction";
// endregion Imports - Components
// region Imports - Services
import api from "@services/api";
// endregion Imports - Services
// region Imports - Store
import { ScreenPlatform } from "@store/ducks/Screen/screen.type";
import { LocationsFormState } from "@store/ducks/Locations/LocationsForm/locations-form.type";
// endregion Imports - Store
// region Imports - Styles
import { Container } from "./styles";
// endregion Imports - Styles

const AlertsHistory: React.FC = () => {

  /* region States */
  const [alerts, setAlerts] = useState<ProcessedAlertHistory[]>([] as Array<ProcessedAlertHistory>);

  const [selectedAlert, setSelectedAlert] = useState<Alert>({} as Alert);
  const [actionType, setActionType] = useState<LocationsFormState["type"]>("details");
  const [isLoadingSelectedAlert, setIsLoadingSelectedAlert] = useState(false);

  const [openExportReportTypeDialog, setOpenExportReportTypeDialog] = useState(false);
  const [openExportScheduledReportDialog, setOpenExportScheduledReportDialog] = useState(false);

  // Used to create export file name
  const [startDate, setStartDate] = useState<string>(dateFns.format(dateFns.subDays(new Date(), 1), "yyyy-MM-dd").concat("T00:00"));
  const [finishDate, setFinishDate] = useState<string>(dateFns.format(new Date(), "yyyy-MM-dd").concat("T23:59"));

  const [loadMoreToggle, setLoadMoreToggle] = useState(false);
  const [openFilterFields, setOpenFilterFields] = useState(true);
  const [numberOfFilteredOptions, setNumberOfFilteredOptions] = useState(0);
  const [hasMoreRecords, setHasMoreRecords] = useState(true);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  /* endregion States */
  /* region Hooks */
  const { screen } = useSelector((screen) => screen);
  const { t } = useTranslation();
  /* endregion Hooks */
  /* region Constants */
  const dataTableSettings: DataTables.Settings = {
    order: [[0, "asc"]],
    columnDefs: [{ className: "dt-center", targets: -1 }]
  };
  const dataTableActions: DataTableActions[] = [
    { ref: ".alert-justify", callback: (rowData: ProcessedAlertHistory) => { handleSelectAlert(rowData.id_alert, "update"); } },
    { ref: ".alert-justified", callback: (rowData: ProcessedAlertHistory) => { handleSelectAlert(rowData.id_alert, "details"); } }
  ];
  const dataTableColumns: DataTableColumns[] = [
    { // Veículo
      title: t(GlobalMessages.vehicle),
      data: (alert: ProcessedAlertHistory) => (alert.vehicle_code),
      defaultContent: "",
      orderable: false,
      filterable: true,
      propertyName: "vehicle_code"
    },
    { // Tipo de Veículo
      title: t(VehiclesModuleMessages.vehicleType),
      className: "print csv",
      data: (alert: ProcessedAlertHistory) => (alert.vehicle_type),
      defaultContent: "",
      filterable: true,
      propertyName: "vehicle_type"
    },
    { // Placa
      title: t(AlertMessages.licensePlate),
      data: (alert: ProcessedAlertHistory) => (alert.license_plate),
      defaultContent: "",
      orderable: false,
      filterable: true,
      propertyName: "license_plate"
    },
    { // Data Ocorrência
      title: t(AlertMessages.occurrenceDate),
      data: (alert: ProcessedAlertHistory) => (alert.occurrence_date),
      filterable: false,
      render: (data: string, type: string) => {
        if ((type === "sort" || type === "type") && data) {
          // Parse "dd/MM/yyyy HH:mm" to timestamp for correct sorting
          const [datePart, timePart] = data.split(" ");
          const [day, month, year] = datePart.split("/");
          const [hour, minute] = timePart?.split(":") ?? ["00", "00"];

          return new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute)).getTime();
        }

        return data;
      }
    },
    { // Motorista
      title: t(GlobalMessages.driver),
      data: (alert: ProcessedAlertHistory) => (alert.driver_name),
      filterable: true,
      propertyName: "driver_name"
    },
    { // Alerta
      title: t(AlertMessages.alert),
      data: (alert: ProcessedAlertHistory) => (alert.alert_type),
      filterable: true,
      propertyName: "alert_type"
    },
    { // Local da ocorrência
      title: t(AlertMessages.occurrenceLocation),
      data: (alert: ProcessedAlertHistory) => (alert.occurrence_location),
      defaultContent: "",
      filterable: true,
      propertyName: "occurrence_location"
    },
    { // Detalhes
      title: t(AlertMessages.details),
      data: (alert: ProcessedAlertHistory) => (alert.details),
      filterable: false
    },
    { // Data Confirmação
      title: t(AlertMessages.confirmationDate),
      data: (alert: ProcessedAlertHistory) => (alert.confirmation_date),
      type: "date",
      filterable: true,
      propertyName: "confirmation_date"
    },
    { // Autorizador
      title: t(AlertMessages.authorizer),
      data: (alert: ProcessedAlertHistory) => (alert.authorizer_name),
      filterable: true,
      propertyName: "authorizer_name"
    },
    { // Justification
      title: t(AlertMessages.justification),
      data: (alert: ProcessedAlertHistory) => (alert.justification ?? ""),
      filterable: false
    },
    { // Observação
      title: t(AlertMessages.observation),
      data: (alert: ProcessedAlertHistory) => (alert.observation),
      filterable: true,
      propertyName: "observation"
    },
    { // Ações
      title: t(DataTableMessages.actions),
      orderable: false,
      searchable: false,
      data: (data) => ReactDOMServer.renderToString(
        <Grid container spacing={1}>
          <Grid item xs sm md lg xl>
            <ButtonTable
              aria-disabled
              className={classNames(
                "action-button", data.confirmation_date ? "alert-justified" : "alert-justify"
              )}
            >
              {data.confirmation_date ? <SearchIcon /> : <DoneAllTwoToneIcon />}
            </ButtonTable>
          </Grid>
        </Grid>
      ),
      filterable: false
    }
  ];
  const dataTableButtons: DataTableButtons[] = [
    {
      name: t(DataTableMessages.buttonsPrint),
      key: "print",
      callback: () => utils.clickButtonDomElement("button-print"),
      extend: "print",
      className: "button-print",
      exportOptions: {
        columns: "th:not(:last-child)"
      }
    },
    {
      name: t(DataTableMessages.buttonsExport),
      callback: () => handleExportData(),
      extend: "csv",
      key: "export",
      fieldSeparator: ";",
      className: "button-export",
      filename: `relatorio_alertas_${startDate}__${finishDate}`,
      exportOptions: {
        columns: "th:not(:last-child)"
      }
    },
    ...(hasMoreRecords ? [{
      name: t(DataTableMessages.moreRecords),
      callback: () => {
        setLoadMoreToggle((prevState) => !prevState);
      },
      key: "moreRecords"
    }] : [])
  ];
  /* endregion Constants */

  /* region Functions */

  const handleSelectAlert = async (alertId: string, actionType: LocationsFormState["type"]) => {
    setIsLoadingSelectedAlert(true);
    setActionType(actionType);

    try {
      const response = await api.post(`alerts/read/${alertId}`);

      if (response.data.status === "success" && response.data.result?.[0]) {
        setSelectedAlert(response.data.result[0]);
      }
    } catch (error) {
      console.error("Error loading alert details:", error);
    } finally {
      setIsLoadingSelectedAlert(false);
    }
  };

  const handleExportData = () => {
    if (hasMoreRecords) setOpenExportReportTypeDialog(true);
    else utils.clickButtonDomElement("button-export");
  };

  const handleApplyFilter = (
    numberOfFilteredOptions: number,
    returnedData: ProcessedAlertHistory[],
    startDate: string,
    finishDate: string,
    pagination?: PaginationProperties
  ) => {
    const loadMore = !!pagination?.offset && pagination.offset > 0;
    const newData = loadMore ? [...alerts, ...returnedData] : returnedData;

    if (loadMore) setAlerts([...alerts, ...returnedData]);
    else setAlerts(returnedData);

    setNumberOfFilteredOptions(numberOfFilteredOptions);
    setAlerts(newData);
    setHasMoreRecords(!!pagination && pagination.totalCount > newData.length);
    setTotalRecords(pagination?.totalCount ?? 0);
    setStartDate(startDate);
    setFinishDate(finishDate);
  };

  /**
   * After close justify modal, update the alert record if justification was completed
   * @param updatedData - Contains the updated authorizer_name, confirmation_date, and justification
   */
  const closeCallbackJustifyAlert = useCallback((updatedData?: { authorizer_name: string, confirmation_date: Date, justification: string }) => {
    // If updatedData is provided, it means the alert was justified and we need to update the record
    if (updatedData && selectedAlert.id_alert) {
      setAlerts(prevAlerts =>
        prevAlerts.map(alert =>
          alert.id_alert === selectedAlert.id_alert
            ? {
              ...alert,
              authorizer_name: updatedData.authorizer_name,
              confirmation_date: utils.formatDateIfHave(updatedData.confirmation_date, "fullDateWithoutSeconds") as string,
              justification: updatedData.justification
            }
            : alert
        )
      );
    }

    setSelectedAlert({} as Alert);
  }, [selectedAlert.id_alert]);
  /* endregion Functions */

  return (
    <>
      <Loading loading={isLoadingSelectedAlert} />
      <Header title={t(AlertMessages.alertsHistoryTitle)} />
      <Container platform={screen.platform as ScreenPlatform} className="page">
        <NewDataTable
          title={t(AlertMessages.alertsHistoryTitle)}
          filters
          data={alerts}
          columns={dataTableColumns}
          actions={dataTableActions}
          settings={dataTableSettings}
          buttons={dataTableButtons}
          onClickFilterButton={() => setOpenFilterFields(true)}
          numberOfFilteredOptions={numberOfFilteredOptions}
          customDataCount={totalRecords}
        />
      </Container>
      {!_.isEmpty(selectedAlert) && !isLoadingSelectedAlert && (
        <AlertDialogJustify selectedAlert={selectedAlert} actionType={actionType} closeCallback={closeCallbackJustifyAlert} />
      )}
      <QueryFilterAlertHistory
        open={openFilterFields}
        onClose={() => setOpenFilterFields(false)}
        onFilter={handleApplyFilter}
        loadMoreToggle={loadMoreToggle}
        generateScheduleReport={{
          openModal: openExportScheduledReportDialog,
          onCloseModal: () => setOpenExportScheduledReportDialog(false)
        }}
      />
      <DialogConfirmAction
        open={openExportReportTypeDialog}
        onClose={() => setOpenExportReportTypeDialog(false)}
        title="Exportar dados"
        style={{ whiteSpace: "pre-line" }}
        actions={[
          {
            text: t(DataTableMessages.modalButtonScheduleExport),
            action: () => {
              setOpenExportScheduledReportDialog(true);
              setOpenExportReportTypeDialog(false);
            }
          },
          {
            text: t(DataTableMessages.modalButtonExportPartial),
            action: () => {
              utils.clickButtonDomElement("button-export");
              setOpenExportReportTypeDialog(false);
            }
          }
        ]}
      >
        {
          t(DataTableMessages.modalExportDescription)
            .replace("_LOADED_", alerts.length.toString())
            .replace("_TOTAL_", totalRecords.toString())
        }
      </DialogConfirmAction>
    </>
  );
};

export default AlertsHistory;
